import { Dispatch } from 'react-redux';

import { Action } from '../../actions';
import { StoreState } from '../../reducers';
import ApiUtils from '../../utils/api';
import { ImportFileState } from './reducer';
import { Api } from '../../typings/api';
import { getOrFetchDriveItemMeta, receiveDriveItems } from '../DriveItemList/actions';
import { createNotification } from '../NotificationBar/actions';
import { downloadThumb } from '../OfflineManager/actions';
import { getUploadingFilesSize } from '../ImportDesktopFile/Toast/selectors';
import { getClientAndResourceIds } from '../Editor/utils/helpers';
import Utils from '../../utils';
import RoutesUtils from '../../utils/routes';
import DriveItemUtils from '../../utils/driveItem';
import UserAgentUtils from '../../utils/userAgent';
import { DownloadPriority } from '../../utils/nativeApi';

export enum FileType {
	COMPONENT = 'component',
}

export enum FileError {
	TYPE = 'wrong_file_type',
	IMPORT = 'file_import_error',
	PROCESSING = 'processing_fail',
}

export enum ImportManagerActionKeys {
	ADD_FILE_IN_IMPORT = '@@importManager/ADD_FILE_IN_IMPORT',
	ADD_FILES_IN_IMPORT = '@@importManager/ADD_FILES_IN_IMPORT',
	UPDATE_FILE_IN_IMPORT = '@@importManager/UPDATE_FILE_IN_IMPORT',
	REMOVE_FILE_FROM_IMPORT = '@@importManager/REMOVE_FILE_FROM_IMPORT',
	REMOVE_FILES_FROM_IMPORT = '@@importManager/REMOVE_FILES_FROM_IMPORT',
	CANCEL_FILES_IMPORT = '@@importManager/CANCEL_FILES_IMPORT',
	RESTART_FILES_IMPORT = '@@importManager/RESTART_FILES_IMPORT',
	ADD_TO_FILES_QUEUE = '@@importManager/ADD_TO_FILES_QUEUE',
	REMOVE_FROM_FILES_QUEUE = '@@importManager/REMOVE_FROM_FILES_QUEUE',
	UPDATE_REMAINING_IMPORT_TIME = '@@importManager/UPDATE_REMAINING_IMPORT_TIME',
}

export interface ImportResponse {
	isError: boolean;
}

interface ImportFileData {
	id: string;
	file: File;
	onImport: (resource: Api.DriveItem) => Promise<void | ImportResponse> | void | ImportResponse;
	folderId?: number;
	shouldDisplayInToast?: boolean;
	fileType?: FileType;
	isOptimizedImageImport?: boolean;
	isPublicationDependency?: boolean;
}

interface AddFileInImportAction extends Action {
	type: ImportManagerActionKeys.ADD_FILE_IN_IMPORT;
	id: string;
	data: ImportFileState;
}

interface AddFilesInImportAction extends Action {
	type: ImportManagerActionKeys.ADD_FILES_IN_IMPORT;
	folderId: number;
	files: File[];
	ids: string[];
}

interface UpdateFileInImportAction extends Action {
	type: ImportManagerActionKeys.UPDATE_FILE_IN_IMPORT;
	id: string;
	data: ImportFileState;
}

interface RemoveFileFromImportAction extends Action {
	type: ImportManagerActionKeys.REMOVE_FILE_FROM_IMPORT;
	id: string;
}

interface RemoveFilesFromImportAction extends Action {
	type: ImportManagerActionKeys.REMOVE_FILES_FROM_IMPORT;
	ids: string[];
}

interface CancelFilesImportAction extends Action {
	type: ImportManagerActionKeys.CANCEL_FILES_IMPORT;
	ids: string[];
}

interface RestartFilesImportAction extends Action {
	type: ImportManagerActionKeys.RESTART_FILES_IMPORT;
	ids: string[];
}

interface AddToFilesQueueAction extends Action {
	type: ImportManagerActionKeys.ADD_TO_FILES_QUEUE;
	ids: string[];
}

interface RemoveFromFilesQueueAction extends Action {
	type: ImportManagerActionKeys.REMOVE_FROM_FILES_QUEUE;
	id: string;
}

interface UpdateRemainingImportTimeAction extends Action {
	type: ImportManagerActionKeys.UPDATE_REMAINING_IMPORT_TIME;
	time: number;
}

export type ImportManagerAction =
	AddFileInImportAction |
	AddFilesInImportAction |
	UpdateFileInImportAction |
	RemoveFileFromImportAction |
	RemoveFilesFromImportAction |
	CancelFilesImportAction |
	RestartFilesImportAction |
	AddToFilesQueueAction |
	RemoveFromFilesQueueAction |
	UpdateRemainingImportTimeAction;

export const addFileInImport = (id: string, data: ImportFileState): AddFileInImportAction => ({
	type: ImportManagerActionKeys.ADD_FILE_IN_IMPORT,
	id,
	data,
});

export const addFilesInImport = (folderId: number, files: File[], ids: string[]): AddFilesInImportAction => ({
	type: ImportManagerActionKeys.ADD_FILES_IN_IMPORT,
	folderId,
	files,
	ids,
});

export const updateFileInImport = (id: string, data: ImportFileState): UpdateFileInImportAction => ({
	type: ImportManagerActionKeys.UPDATE_FILE_IN_IMPORT,
	id,
	data,
});

export const removeFileFromImport = (id: string) => (dispatch: Dispatch<StoreState>) => {
	dispatch({
		type: ImportManagerActionKeys.REMOVE_FILE_FROM_IMPORT,
		id,
	});

	Utils.cancelRequest(id);
};

export const removeFilesFromImport = (ids: string[]) => (dispatch: Dispatch<StoreState>) => {
	dispatch({
		type: ImportManagerActionKeys.REMOVE_FILES_FROM_IMPORT,
		ids,
	});

	ids.forEach(id => Utils.cancelRequest(id));
};

export const cancelFileImport = (id: string) => (dispatch: Dispatch<StoreState>) => {
	dispatch(removeFromFilesQueue(id));
	dispatch({
		type: ImportManagerActionKeys.UPDATE_FILE_IN_IMPORT,
		id,
		data: {
			isImportCanceled: true,
		},
	});

	Utils.cancelRequest(id);
};

export const cancelFilesImport = (ids: string[]) => (dispatch: Dispatch<StoreState>) => {
	dispatch({
		type: ImportManagerActionKeys.CANCEL_FILES_IMPORT,
		ids,
	});

	ids.forEach(id => Utils.cancelRequest(id));
};

export const restartFilesImport = (ids: string[]) => (dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
	dispatch({
		type: ImportManagerActionKeys.RESTART_FILES_IMPORT,
		ids,
	});

	const { importManager: { byId } } = getState();
	const files = ids.map(id => byId[id].file as File);

	dispatch(importDesktopFiles(files, ids));
};

export const addToFilesQueue = (ids: string[]): AddToFilesQueueAction => ({
	type: ImportManagerActionKeys.ADD_TO_FILES_QUEUE,
	ids,
});

export const removeFromFilesQueue = (id: string): RemoveFromFilesQueueAction => ({
	type: ImportManagerActionKeys.REMOVE_FROM_FILES_QUEUE,
	id,
});

export const updateRemainingImportTime = (time: number): UpdateRemainingImportTimeAction => ({
	type: ImportManagerActionKeys.UPDATE_REMAINING_IMPORT_TIME,
	time,
});

export const importFile = ({
	id,
	file,
	onImport,
	folderId,
	shouldDisplayInToast,
	fileType,
	isOptimizedImageImport,
	isPublicationDependency,
}: ImportFileData) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	const {
		galleryInfos: { config },
		driveList,
	} = getState();

	let publicationParentFolderId: number | undefined;

	if (isPublicationDependency) {
		const { originalGId: resourceId } = getClientAndResourceIds();
		publicationParentFolderId = Utils.isEmbedMode()
			? DriveItemUtils.getParentFolderId(driveList.driveItems[resourceId])
			: Utils.getSourcesDirId(driveList, config);
	}

	const parentFolderId = folderId ||
		publicationParentFolderId ||
		Utils.getPrivateDirId(driveList, config) as number;

	let loadedBytesAtPrevSecond = 0;

	const onImportProgress = (e: ProgressEvent) => {
		const percentage = Math.floor((e.loaded / e.total) * 100);

		if (!shouldDisplayInToast) {
			dispatch(updateFileInImport(id, {
				percentage,
			}));
			return;
		}

		// At the first second of file import loaded bytes are small which causes remainingTime
		// to be a very big. To prevent it, remainingTime is not calculated at the first second
		if (!loadedBytesAtPrevSecond) {
			loadedBytesAtPrevSecond = e.loaded;
			return;
		}

		const uploadingFilesSize = getUploadingFilesSize(getState());
		const loadSpeed = e.loaded - loadedBytesAtPrevSecond;
		loadedBytesAtPrevSecond = e.loaded;
		// e.total and uploadingFilesSize (which is calculated as a sum of file.size of all files)
		// are not absolutely equal. It can cause remainingBytes to be a negative value.
		// Math.max is used to prevent it
		const remainingBytes = Math.max(uploadingFilesSize - e.loaded, 0);
		const remainingTime = remainingBytes / loadSpeed;

		dispatch(updateFileInImport(id, {
			percentage,
		}));
		dispatch(updateRemainingImportTime(remainingTime));
	};

	const uploadFile = async (): Promise<Api.DriveItem> => {
		let data: Api.ImportFileData | Api.AspDriveResourceItem;

		if (fileType === FileType.COMPONENT) {
			data = await ApiUtils.createComponent(config.clientId, parentFolderId, file, isPublicationDependency);
		} else {
			data = await ApiUtils.importFile(config.clientId, parentFolderId, file, onImportProgress, id, isOptimizedImageImport, isPublicationDependency);
		}

		const globalId = 'globalId' in data
			? data.globalId
			: data.resourceGId;
		const resource = await ApiUtils.getMeta(globalId);
		return resource;
	};

	const RESOURCE_PROCESSING_DELAY: number = 2000;
	let resourceProcessingTimer: number;
	const getResourceProcessing = async (resourceGId: number): Promise<void> => {
		try {
			const resource = await ApiUtils.getMeta(resourceGId);
			const { status } = resource;

			if (!status || status === Api.AssetItemStatus.LIVE) {
				clearInterval(resourceProcessingTimer);
				const parentId = DriveItemUtils.getParentFolderId(resource);
				dispatch(getOrFetchDriveItemMeta(parentId, true, true, true));

				if (shouldDisplayInToast) {
					dispatch(updateFileInImport(id, {
						isImportFinished: true,
					}));
				} else {
					dispatch(removeFileFromImport(id));
				}
			} else if (status === Api.AssetItemStatus.ERROR) {
				clearInterval(resourceProcessingTimer);
				dispatch(updateFileInImport(id, {
					error: FileError.IMPORT,
					percentage: 100,
				}));
			}
		} catch (error) {
			clearInterval(resourceProcessingTimer);
			dispatch(updateFileInImport(id, {
				error: FileError.IMPORT,
				percentage: 100,
			}));
		}
	};

	if (!shouldDisplayInToast) {
		dispatch(addFileInImport(id, {
			id,
			folderId,
			percentage: 0,
			error: null,
		}));
	}

	try {
		let resource = await uploadFile();
		const { status, id: resourceGId } = resource;
		const parentId = DriveItemUtils.getParentFolderId(resource);

		// server doesn't return 'icon' field until video processing is finished which leads to display of default video icon instead of cover image
		// for videos 'icon' always has the same form so adding it manually loads a cover image immediately
		if (resource.type === Api.DriveItemType.Movie && !resource.icon) {
			resource.icon = `$media$${resourceGId}/cover.jpg`;
		}

		dispatch(receiveDriveItems({ [resourceGId]: resource }));

		if (UserAgentUtils.isMobileApp) {
			dispatch(downloadThumb(resourceGId, DownloadPriority.Normal, false, resource));
		}

		if (!status || status === Api.AssetItemStatus.LIVE) {
			const importResponse = await onImport(resource);
			dispatch(getOrFetchDriveItemMeta(parentId, true, true, true));

			if (!importResponse || !importResponse.isError) {
				if (shouldDisplayInToast) {
					dispatch(updateFileInImport(id, {
						isImportFinished: true,
					}));
				} else {
					dispatch(removeFileFromImport(id));
				}
			}
		} else if (status === Api.AssetItemStatus.ERROR) {
			dispatch(updateFileInImport(id, {
				error: FileError.IMPORT,
			}));
		} else {
			if (shouldDisplayInToast) {
				dispatch(updateFileInImport(id, {
					isImportFinished: true,
				}));
				await onImport(resource);
				dispatch(getOrFetchDriveItemMeta(parentId, true, true, true));
			} else {
				dispatch(updateFileInImport(id, {
					isProcessing: true,
				}));
				await onImport(resource);
				resourceProcessingTimer = window.setInterval(() => {
					getResourceProcessing(resourceGId);
				}, RESOURCE_PROCESSING_DELAY);
			}
		}
	} catch (error) {
		const {
			importManager: {
				byId: {
					[id]: { isImportCanceled = false } = {},
				},
			},
		} = getState();

		// to prevent displaying error in Toast if user canceled import
		if (!isImportCanceled) {
			dispatch(updateFileInImport(id, {
				error: FileError.IMPORT,
				percentage: 100,
			}));
		}
	}
};

export const importDesktopFiles = (files: File[], prevFilesIds?: string[]) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		const parentFolderId = Utils.getFolderIdForNewResource();

		if (!parentFolderId) {
			return dispatch(createNotification('error.file_import_error'));
		}

		const { routerParamsHistory } = getState();
		const currentOriginParam = RoutesUtils.getCurrentOriginParam(routerParamsHistory);

		if (currentOriginParam !== parentFolderId.toString()) {
			RoutesUtils.redirectToParentDir(parentFolderId);
		}

		const {
			importManager: {
				filesQueue: prevFilesQueue,
			},
		} = getState();
		const filesIds = prevFilesIds || files.map(file => Utils.generateId());

		// if prevFilesIds is presented, it means that these files are already added in
		// importManager and they are restarted
		if (!prevFilesIds) {
			dispatch(addFilesInImport(parentFolderId, files, filesIds));
		}

		dispatch(addToFilesQueue(filesIds));

		// in case some prev files are being imported, while loop shouldn't be recalled
		if (prevFilesQueue.length > 0) {
			return;
		}

		let { importManager: { filesQueue } } = getState();

		while (filesQueue.length > 0) {
			const id = filesQueue[0];
			const {
				importManager: {
					byId: {
						[id]: {
							file = undefined,
							folderId = parentFolderId,
						} = {},
					},
				},
			} = getState();

			if (file) {
				await dispatch(importFile({
					id,
					file,
					onImport: () => undefined,
					folderId,
					shouldDisplayInToast: true,
				}));
			}

			dispatch(removeFromFilesQueue(id));
			filesQueue = getState().importManager.filesQueue;
		}
	} catch (error) {
		dispatch(createNotification('error.file_import_error'));
	}
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/ImportManager/actions.ts